import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

// Mined from designSystem/Breadcrumbs/Breadcrumbs.module.scss. The variant axis
// is Figma's `Levels`; every level shares one stylesheet. Ancestors are Link sm,
// so their hover, press, focus and disabled paint lives in the Link anatomy.

const parts: readonly AnatomyPart[] = [
    {
        id: "surface",
        label: "Row",
        element: "<ol class=.list> inside <nav aria-label>",
    },
    {
        id: "itemLabel",
        label: "Ancestor",
        element:
            'Link size="sm" (onClick, href or render), or static span.text',
    },
    {
        id: "separator",
        label: "Separator",
        element:
            "ChevronRightSmallIcon (.separator) after each item; hidden on the last",
    },
    {
        id: "label",
        label: "Current item",
        element: 'span.text[aria-current="page"], truncates with a tooltip',
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
];

const sharedBindings: readonly AnatomyBinding[] = [
    { partId: "surface", property: "gap", token: "--space-8" },
    {
        partId: "surface",
        property: "gap (item to separator)",
        token: "--space-8",
        note: "Each li is its own inline-flex row: content, then separator.",
    },
    ...anatomyTextStyle({
        scale: "s",
        weight: "500",
        mixin: "text-s-14-medium",
        partId: "itemLabel",
        note: "Link sm's text style; static ancestors match it on span.text.",
    }),
    { partId: "itemLabel", property: "color", token: "--fg-primary" },
    { partId: "separator", property: "width", literal: "8px" },
    { partId: "separator", property: "height", literal: "8px" },
    { partId: "separator", property: "color", token: "--fg-tertiary" },
    ...anatomyTextStyle({
        scale: "s",
        weight: "500",
        mixin: "text-s-14-medium",
        partId: "label",
    }),
    { partId: "label", property: "color", token: "--fg-primary" },
    {
        partId: "label",
        property: "text-overflow",
        literal: "ellipsis",
        note: "Only the current li shrinks (flex: 0 1 auto; min-width: 0).",
    },
];

const levelVariant = (level: number): AnatomyVariant => ({
    id: String(level),
    label: String(level),
    selector: "",
    restingBindings: [],
    stateBindings: {},
});

const variants: readonly AnatomyVariant[] = [1, 2, 3].map(levelVariant);

export const breadcrumbsAnatomy: ComponentAnatomySpec = {
    label: "Breadcrumbs",
    schematic: "breadcrumbs",
    rootSelector: ".list",
    variantAxisLabel: "Levels",
    parts,
    states,
    sharedBindings,
    variants,
};
